import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, Sparkles, ShieldCheck, Zap, Clock, Building2 } from "lucide-react";

const trustBadges = [
  { icon: Sparkles, label: "Funding up to", value: "$5,000,000" },
  { icon: Zap, label: "Approvals in", value: "Same Day" },
  { icon: Clock, label: "Funds in", value: "24 Hours" },
  { icon: Building2, label: "Businesses helped", value: "15,000+" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-12 md:pt-56 md:pb-32 overflow-hidden hero-stripe-overlay"
    >
      {/* Background layers — desktop only (heavy blur kills mobile GPU) */}
      <div className="hidden md:block absolute inset-0 grid-pattern opacity-50" />
      <div className="hidden md:block absolute inset-0 radial-glow-blue" />
      <div className="hidden md:block absolute inset-0 radial-glow-green" />
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#0052FF]/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-6 md:mb-8"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-[#00FFA3] animate-pulse" />
              <span className="text-xs font-mono tracking-widest text-zinc-300">CAPITAL OPEN — LIVE NOW</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[0.95] tracking-tighter uppercase"
              data-testid="hero-headline"
            >
              Business Funding
              <br />
              Built For{" "}
              <span className="relative inline-block">
                <span className="text-gradient-neon">Momentum.</span>
                <motion.span
                  className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-[#0052FF] to-[#00FFA3] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.2, delay: 0.6 }}
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 md:mt-8 text-base md:text-xl text-zinc-400 max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              Fast approvals. Flexible capital. Up to <span className="text-[#00FFA3] font-bold">$5M</span> funded in 24 hours.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-7 md:mt-10 flex flex-col sm:flex-row gap-3 md:gap-4"
            >
              <Link
                to="/application"
                data-testid="hero-cta-apply"
                className="group relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#00FFA3] text-black font-semibold animate-pulse-glow hover:scale-[1.02] transition-transform"
              >
                Apply Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#calculator"
                data-testid="hero-cta-eligibility"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full glass text-white font-semibold hover:border-[#0052FF]/50 transition-all"
              >
                Check Eligibility
              </a>
              <a
                href="tel:+19086767979"
                data-testid="hero-cta-call"
                className="hidden md:inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-white font-semibold hover:text-[#00FFA3] transition-colors"
              >
                <PhoneCall className="w-4 h-4" />
                Speak To An Advisor
              </a>
            </motion.div>

            {/* Trust strip — desktop only */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="hidden md:grid mt-12 grid-cols-2 md:grid-cols-4 gap-4"
              data-testid="hero-trust-badges"
            >
              {trustBadges.map((b, i) => (
                <div
                  key={i}
                  className="glass rounded-xl p-4 hover:border-[#0052FF]/40 transition-colors"
                  data-testid={`trust-badge-${i}`}
                >
                  <b.icon className="w-4 h-4 text-[#00FFA3] mb-2" />
                  <p className="text-[10px] tracking-widest uppercase text-zinc-500 font-mono">{b.label}</p>
                  <p className="font-display font-bold text-lg mt-0.5">{b.value}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: dashboard mockup — desktop only (heavy animations) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block lg:col-span-5 relative"
          >
            <HeroDashboard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HeroDashboard() {
  const bars = [42, 58, 49, 71, 65, 82, 91, 78, 88, 96, 92, 100];

  return (
    <div className="relative">
      {/* Main dashboard card */}
      <div className="glass-strong rounded-3xl p-6 relative overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-zinc-500 font-mono">Approval Velocity</p>
            <p className="font-display text-2xl font-bold mt-1">$12.4M <span className="text-[#00FFA3] text-sm font-mono">+24.8%</span></p>
          </div>
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#00FFA3]/80" />
          </div>
        </div>

        {/* Chart */}
        <div className="h-44 flex items-end gap-2 mb-6 relative">
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="border-t border-white/5 w-full" />
            ))}
          </div>
          {bars.map((h, i) => (
            <motion.div
              key={i}
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.06, ease: "easeOut" }}
              style={{ height: `${h}%`, transformOrigin: "bottom" }}
              className={`flex-1 rounded-t ${
                i === bars.length - 1
                  ? "bg-gradient-to-t from-[#0052FF] to-[#00FFA3]"
                  : "bg-gradient-to-t from-[#0052FF]/70 to-[#0052FF]/30"
              }`}
            />
          ))}
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/5">
          <div>
            <p className="text-[9px] uppercase text-zinc-500 font-mono tracking-widest">Approval</p>
            <p className="font-display font-bold text-lg text-[#00FFA3]">95%</p>
          </div>
          <div>
            <p className="text-[9px] uppercase text-zinc-500 font-mono tracking-widest">Time to Fund</p>
            <p className="font-display font-bold text-lg">24 hr</p>
          </div>
          <div>
            <p className="text-[9px] uppercase text-zinc-500 font-mono tracking-widest">Avg Offer</p>
            <p className="font-display font-bold text-lg">$184K</p>
          </div>
        </div>
      </div>

      {/* Floating cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute -top-6 -left-6 glass rounded-2xl p-3 px-4 animate-float"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00FFA3]/20 grid place-items-center">
            <ShieldCheck className="w-4 h-4 text-[#00FFA3]" />
          </div>
          <div>
            <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Funded</p>
            <p className="font-display font-bold text-sm">$250,000</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="absolute -bottom-6 -right-4 glass rounded-2xl p-3 px-4 animate-float-slow"
      >
        <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Renewal Rate</p>
        <p className="font-display font-bold text-xl text-gradient-neon">94%</p>
      </motion.div>
    </div>
  );
}
